import {WriteButton} from '../common/buttons/WriteActions';
import React, {useEffect, useState} from 'react';
import {Alert, Box, InputAdornment, Snackbar, TextField} from '@mui/material';
import {getStyles} from './recipeCommon';
import {getMedia} from '../../common/mediaQuery';
import {RecipeTable} from "./recipe/RecipeTable";
import {recipeAPI} from "../../services/RecipeServices";
import {useNavigate} from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import {Clear} from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import useDebounce from "../../hooks/useDebounce";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {usePageLimit} from "../../hooks/usePageLimit";
import {getApiErrorKind, getApiErrorMessage, getApiTraceId} from '../../common/apiErrors';
import TraceIdFeedback from '../feedback/TraceIdFeedback';
import {Surface} from '../common/layout/Surface';
import {ContentSkeleton} from '../common/loading/ContentSkeleton';

function Recipes() {
    const {isFullHD} = getMedia();
    const {wrapperItems, boxItemsLoading} = getStyles();
    const navigate = useNavigate();
    const [isAllRecipesIsLoading, setIsAllRecipesIsLoading] = useState(false);
    const {t} = useAppTranslation();

    // api data state
    const [page, setPage] = useState(0);
    const [limit, setLimit] = usePageLimit('recipes');

    const handlePageChange = (newPage: number) => setPage(newPage);

    const handlePageSizeChange = (newSize: number) => {
        setLimit(newSize);
        setPage(0);
    }
    const handleSortRequest = (
        id: 'name' | 'price',
        direction: 'asc' | 'desc'
    ) => {
        setDescending(direction === 'desc');
        setPage(0);

        // Сбрасываем все флаги и включаем только нужный
        setOrderByName(id === 'name');
    };
    const [searchPhrase, setSearchPhrase] = useState('');
    const debouncedSearchPhrase = useDebounce(searchPhrase, 600);

    const [orderByName, setOrderByName] = useState(true);
    const [descending, setDescending] = useState(false);
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');

    const {data: response, isLoading, error, isSuccess} = recipeAPI.useGetRecipesV2Query({
        page,
        pageSize: limit,
        searchPhrase: debouncedSearchPhrase.trim(),
        descending,
    });

    useEffect(() => {
        if (limit < 1 || limit > 200) setLimit(15);
    }, [limit, setLimit]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearchPhrase]);

    useEffect(() => {
        if (!error || getApiErrorKind(error) !== 'server') return;
        setServerToast(getApiErrorMessage(error, t));
        setTraceId(getApiTraceId(error));
    }, [error, t]);

    useEffect(() => {
        setIsAllRecipesIsLoading(!isSuccess);
        if (response && page > 0 && page >= response.pageCount) setPage(0);
    }, [isSuccess, page, response]);

    return (
        <Box sx={{...wrapperItems, p: 0, ...(isFullHD ? {minHeight: '830px'} : {})}}>
            <Box sx={{display: 'flex', justifyContent: {xs: 'stretch', md: 'flex-end'}, mb: 2, '& .MuiButton-root': {width: {xs: '100%', md: 'auto'}}}}>
                    <WriteButton
                        variant="contained"
                        startIcon={<AddIcon/>}
                        onClick={() => navigate("new")}
                    >
                        {`${t('recipes:create_recipe')}`}
                    </WriteButton>
            </Box>

            {(isLoading || isAllRecipesIsLoading) && (
                <Box sx={boxItemsLoading}><ContentSkeleton variant="list"/></Box>
            )}

            {isSuccess && !isLoading && response && (
                <Surface sx={{width: '100%'}}>
                    <Box sx={{p: 2}}>
                        <TextField
                            label={t('recipes:search_recipes')}
                            variant="outlined"
                            focused
                            size="small"
                            fullWidth
                            value={searchPhrase}
                            onChange={(e) => {
                                setSearchPhrase(e.target.value);
                            }}
                            InputProps={{
                                endAdornment: searchPhrase ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSearchPhrase("");
                                            }}
                                        >
                                            <Clear/>
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </Box>
                    <RecipeTable
                        data={response.data}
                        totalCount={response.totalCount}
                        pageSize={response.pageSize}
                        currentPage={response.page}
                        descending={descending}
                        orderByName={orderByName}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSortRequest={handleSortRequest}
                    />
                </Surface>)}
            {error && getApiErrorKind(error) !== 'server' && <Alert severity="error">{getApiErrorMessage(error, t)}</Alert>}
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}><Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert></Snackbar>
        </Box>
    );
}

export default Recipes;
